import { useState, useMemo } from 'react'
import { X, CheckCircle2, XCircle, FileText, Clock, Save } from 'lucide-react'
import { hasPendingGrading, answerEarned, isPendingAnswer } from '../../utils/quizGrading.js'

// 서술형 답안 판별 — 문항이 삭제된 구 답안은 채점 대기 여부로 추정
const isEssayAnswer = (ans, q) => (q ? q.type === 'essay' : isPendingAnswer(ans))

// props:
//   attempts: QuizAttempt[]   — 표시할 응시 이력
//   students: User[]          — id로 학생 매핑 (학생 이름·학년 표시)
//   quizSets: QuizSet[]       — id로 회차 매핑 (회차 제목 표시)
//   quizQuestions: QuizQuestion[] — 상세 모달용
//   emptyMessage?: string
//   onUpdateGrading?: (attemptId, gradingByQid) => Promise<updatedAttempt>
//     — 있으면 상세 모달에서 채점 저장 가능. 단답형은 정오 토글(boolean),
//       서술형은 0~배점 점수 입력(number, null = 채점 대기)
export default function QuizResultsTable({ attempts, students, quizSets, quizQuestions, emptyMessage, onUpdateGrading }) {
  const [filterGrade, setFilterGrade] = useState('all')
  const [filterSetId, setFilterSetId] = useState('all')
  const [openAttempt, setOpenAttempt] = useState(null)
  const [gradingDraft, setGradingDraft] = useState({})
  const [savingGrades, setSavingGrades] = useState(false)

  // 채점 초안 — 단답형은 boolean(정오), 서술형은 number|null(득점, null = 채점 대기)
  const draftFrom = (attempt) =>
    Object.fromEntries(attempt.answers.map((ans) => {
      const q = (questionsBySet[attempt.quizSetId] || []).find((qq) => qq.id === ans.questionId)
      return [ans.questionId, isEssayAnswer(ans, q) ? answerEarned(ans) : ans.isCorrect]
    }))

  const openDetail = (attempt) => {
    setOpenAttempt(attempt)
    setGradingDraft(draftFrom(attempt))
  }

  const handleSaveGrading = async () => {
    if (!onUpdateGrading || !openAttempt || !gradingDirty || savingGrades) return
    setSavingGrades(true)
    try {
      const updated = await onUpdateGrading(openAttempt.id, gradingDraft)
      if (updated) {
        setOpenAttempt(updated)
        setGradingDraft(draftFrom(updated))
      }
    } catch {
      // 저장 실패는 전역 Toast가 표면화한다.
    } finally {
      setSavingGrades(false)
    }
  }

  const studentById = useMemo(() => Object.fromEntries(students.map((s) => [s.id, s])), [students])
  const setById     = useMemo(() => Object.fromEntries(quizSets.map((s) => [s.id, s])), [quizSets])
  const questionsBySet = useMemo(() => {
    const map = {}
    quizQuestions.forEach((q) => {
      if (!map[q.quizSetId]) map[q.quizSetId] = []
      map[q.quizSetId].push(q)
    })
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.orderNo - b.orderNo))
    return map
  }, [quizQuestions])

  const grades = useMemo(() => {
    const set = new Set(quizSets.map((s) => s.grade).filter(Boolean))
    return ['all', ...Array.from(set)]
  }, [quizSets])

  const filtered = useMemo(() => {
    return attempts
      .filter((a) => {
        if (filterSetId !== 'all' && a.quizSetId !== filterSetId) return false
        if (filterGrade !== 'all') {
          const s = setById[a.quizSetId]
          if (!s || s.grade !== filterGrade) return false
        }
        return true
      })
      .sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''))
  }, [attempts, filterGrade, filterSetId, setById])

  const setOptions = useMemo(() => {
    if (filterGrade === 'all') return quizSets
    return quizSets.filter((s) => s.grade === filterGrade)
  }, [quizSets, filterGrade])

  const openSet = openAttempt ? setById[openAttempt.quizSetId] : null
  // 응시 시점 순서대로 표시 — attempt.answers 순서가 응시 당시의 문제 순서를 그대로 담고 있음
  const openQuestionMap = openAttempt
    ? Object.fromEntries((questionsBySet[openAttempt.quizSetId] || []).map((q) => [q.id, q]))
    : {}
  const openStudent = openAttempt ? studentById[openAttempt.studentId] : null

  const gradingDirty = !!openAttempt &&
    openAttempt.answers.some((ans) => {
      const q = openQuestionMap[ans.questionId]
      const current = isEssayAnswer(ans, q) ? answerEarned(ans) : ans.isCorrect
      return gradingDraft[ans.questionId] !== current
    })

  return (
    <div className="space-y-3">
      {/* 필터 */}
      <div className="flex gap-2 text-xs">
        <select
          value={filterGrade}
          onChange={(e) => { setFilterGrade(e.target.value); setFilterSetId('all') }}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white"
        >
          {grades.map((g) => (
            <option key={g} value={g}>{g === 'all' ? '전체 학년' : g}</option>
          ))}
        </select>
        <select
          value={filterSetId}
          onChange={(e) => setFilterSetId(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white"
        >
          <option value="all">전체 회차</option>
          {setOptions.map((s) => (
            <option key={s.id} value={s.id}>{s.subject ? `${s.subject} · ` : ''}{s.title}</option>
          ))}
        </select>
      </div>

      {/* 테이블 (모바일도 고려한 카드 + 데스크탑 테이블 하이브리드) */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center text-sm text-gray-400">
          {emptyMessage || '응시 이력이 없습니다.'}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* 데스크탑 헤더 */}
          <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-[11px] font-bold text-gray-500 uppercase tracking-wide">
            <div className="col-span-3">학생</div>
            <div className="col-span-1">학년</div>
            <div className="col-span-4">회차</div>
            <div className="col-span-2">점수</div>
            <div className="col-span-2">제출일</div>
          </div>
          <ul className="divide-y divide-gray-100">
            {filtered.map((a) => {
              const stu = studentById[a.studentId]
              const set = setById[a.quizSetId]
              const pct = a.total > 0 ? Math.round((a.score / a.total) * 100) : 0
              const submitted = a.submittedAt ? a.submittedAt.slice(0, 10) : '—'
              return (
                <li key={a.id}>
                  <button
                    onClick={() => openDetail(a)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                  >
                    <div className="sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center flex flex-col gap-1">
                      <p className="sm:col-span-3 text-sm font-semibold text-gray-800">{stu?.name ?? a.studentId}</p>
                      <p className="sm:col-span-1 text-xs text-gray-500">{stu?.grade ?? '—'}</p>
                      <p className="sm:col-span-4 text-xs text-gray-600 truncate">
                        {set ? `${set.subject ? `${set.subject} · ` : ''}${set.title}` : a.quizSetId}
                      </p>
                      <p className="sm:col-span-2 text-sm font-bold">
                        <span className={pct >= 80 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : 'text-red-600'}>
                          {a.score} / {a.total}
                        </span>
                        <span className="text-[11px] text-gray-400 ml-1">({pct}%)</span>
                        {hasPendingGrading(a) && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded-full ml-1.5 align-middle">채점중</span>
                        )}
                      </p>
                      <p className="sm:col-span-2 text-xs text-gray-400">{submitted}</p>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* 상세 모달 */}
      {openAttempt && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-xl max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-emerald-600" />
                <div>
                  <p className="text-sm font-bold text-gray-800">{openStudent?.name ?? openAttempt.studentId}</p>
                  <p className="text-[11px] text-gray-500">{openSet?.title}</p>
                </div>
              </div>
              <button onClick={() => setOpenAttempt(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto p-4 space-y-2">
              <div className="bg-emerald-50 rounded-xl p-3 mb-1">
                <p className="text-xs text-emerald-700">
                  <span className="font-bold">{openAttempt.score} / {openAttempt.total}</span>
                  <span className="ml-1">점</span>
                  <span className="ml-2 text-emerald-600">
                    ({openAttempt.total > 0 ? Math.round(openAttempt.score / openAttempt.total * 100) : 0}%)
                  </span>
                </p>
                <p className="text-[11px] text-emerald-600 mt-0.5">
                  제출 {openAttempt.submittedAt ? openAttempt.submittedAt.slice(0, 16).replace('T', ' ') : '—'}
                </p>
              </div>

              {openAttempt.answers.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">
                  {openSet?.isScoreOnly
                    ? '점수전용 회차 — 외부 시험 점수라 문항별 결과가 없습니다.'
                    : '문제 정보를 불러오지 못했습니다.'}
                </p>
              ) : (
                openAttempt.answers.map((ans, idx) => {
                  const q = openQuestionMap[ans.questionId]
                  const essay = isEssayAnswer(ans, q)
                  // 배점 — 응시 스냅샷 우선, 구 답안은 현재 문항 배점(없으면 1점)
                  const pts = Number.isFinite(ans.points) ? ans.points : (q?.points ?? 1)
                  const draft = gradingDraft[ans.questionId]
                  // 득점으로 통일해 상태 판정 — 편집 가능하면 draft, 아니면 저장값
                  const earned = onUpdateGrading
                    ? (essay ? (Number.isFinite(draft) ? draft : null) : draft === true ? pts : draft === false ? 0 : null)
                    : answerEarned(ans)
                  const pending = earned == null
                  const full = !pending && earned >= pts
                  const zero = !pending && earned === 0
                  const tone = pending || (!full && !zero) ? 'amber' : full ? 'emerald' : 'red'
                  return (
                    <div
                      key={ans.questionId}
                      className={`rounded-xl p-3 border ${
                        tone === 'amber' ? 'border-amber-100 bg-amber-50/40'
                          : tone === 'emerald' ? 'border-emerald-100 bg-emerald-50/40'
                          : 'border-red-100 bg-red-50/40'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-1.5">
                        {pending
                          ? <Clock size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                          : full
                            ? <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                            : zero
                              ? <XCircle      size={16} className="text-red-500     flex-shrink-0 mt-0.5" />
                              : <CheckCircle2 size={16} className="text-amber-500   flex-shrink-0 mt-0.5" />
                        }
                        <p className="text-xs text-gray-800 leading-relaxed whitespace-pre-wrap flex-1">
                          <span className="text-[10px] font-bold text-gray-400 mr-1">Q{idx + 1}.</span>
                          {q?.question ?? <span className="text-gray-400 italic">(삭제된 문제)</span>}
                        </p>
                        {essay && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">서술형</span>
                        )}
                      </div>
                      <div className="ml-5 text-[11px] space-y-0.5">
                        <p>
                          <span className="text-gray-400">답: </span>
                          <span className={`whitespace-pre-wrap font-semibold ${
                            pending ? 'text-gray-700' : full ? 'text-emerald-700' : zero ? 'text-red-700' : 'text-amber-700'
                          }`}>
                            {ans.raw || '(미입력)'}
                          </span>
                        </p>
                        {q && q.acceptedAnswers.length > 0 && (
                          <p>
                            <span className="text-gray-400">정답: </span>
                            <span className="text-gray-800 font-semibold">{q.acceptedAnswers[0]}</span>
                            {q.acceptedAnswers.length > 1 && (
                              <span className="text-gray-400"> · {q.acceptedAnswers.slice(1).join(', ')}</span>
                            )}
                          </p>
                        )}
                        {essay && !pending && !onUpdateGrading && (
                          <p>
                            <span className="text-gray-400">득점: </span>
                            <span className={`font-semibold ${full ? 'text-emerald-700' : zero ? 'text-red-700' : 'text-amber-700'}`}>
                              {earned} / {pts}점
                            </span>
                          </p>
                        )}
                        {onUpdateGrading && (essay ? (
                          // 서술형 — 0~배점 점수 입력 (빈 입력 = 채점 대기)
                          <div className="flex items-center gap-1.5 pt-1.5">
                            <input
                              type="number"
                              min={0}
                              max={pts}
                              value={draft ?? ''}
                              onChange={(e) => {
                                const n = Number(e.target.value)
                                setGradingDraft((d) => ({
                                  ...d,
                                  [ans.questionId]: e.target.value === '' || !Number.isFinite(n)
                                    ? null
                                    : Math.min(Math.max(n, 0), pts),
                                }))
                              }}
                              className="w-16 px-2 py-1 rounded-lg border border-gray-200 text-[11px] font-semibold"
                            />
                            <span className="text-gray-500 font-semibold">/ {pts}점</span>
                            {pending && <span className="text-amber-600 font-semibold ml-1">채점 대기</span>}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 pt-1.5">
                            <button
                              type="button"
                              onClick={() => setGradingDraft((d) => ({ ...d, [ans.questionId]: true }))}
                              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition ${
                                draft === true
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                              }`}
                            >
                              정답
                            </button>
                            <button
                              type="button"
                              onClick={() => setGradingDraft((d) => ({ ...d, [ans.questionId]: false }))}
                              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition ${
                                draft === false
                                  ? 'bg-red-600 text-white border-red-600'
                                  : 'bg-white text-red-700 border-red-200 hover:bg-red-50'
                              }`}
                            >
                              오답
                            </button>
                            {pending && <span className="text-amber-600 font-semibold ml-1">채점 대기</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => setOpenAttempt(null)}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-sm font-semibold text-gray-700"
              >
                닫기
              </button>
              {onUpdateGrading && (
                <button
                  onClick={handleSaveGrading}
                  disabled={!gradingDirty || savingGrades}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-sm font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-40"
                >
                  <Save size={15} />
                  {savingGrades ? '저장 중…' : '채점 저장'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
